/**
 * Multi-location trip days ("Antwerp → London").
 *
 * A day's `locations` is an ordered array of { name, lat, lng } stops:
 * [0] is where the day starts (wake up), the last entry is where it ends
 * (sleep). The legacy `location` / `latitude` / `longitude` fields mirror
 * the FIRST stop — single-location consumers (weather, travel tips, "Fill
 * your day" search, map centering) anchor on where the user spends the
 * daytime. Always mutate a day's locations via buildDayLocationsPatch (below)
 * so the mirror fields stay in sync.
 */

export const MAX_DAY_LOCATIONS = 6;

/**
 * The day's ordered stop list. Falls back to a 0/1-entry array derived from
 * the legacy `location` field, so callers can treat every day uniformly.
 */
export function getDayLocations(day) {
  if (Array.isArray(day?.locations) && day.locations.length > 0) {
    return day.locations.filter(l => l && String(l.name || '').trim());
  }
  if (day?.location) {
    return [{ name: day.location, lat: day.latitude ?? null, lng: day.longitude ?? null }];
  }
  return [];
}

/** "Antwerp → London" — the display chain for day headers. */
export function formatDayLocations(day, separator = ' → ') {
  return getDayLocations(day).map(l => l.name).join(separator);
}

/** Where the day starts (the weather/search anchor). */
export function primaryDayLocationName(day) {
  const locs = getDayLocations(day);
  return locs.length > 0 ? locs[0].name : '';
}

/** Where the day ends — what the NEXT day should inherit as its location. */
export function lastDayLocationName(day) {
  const locs = getDayLocations(day);
  return locs.length > 0 ? locs[locs.length - 1].name : '';
}

/** True when the day spans more than one location (a travel day). */
export function isMultiLocationDay(day) {
  return getDayLocations(day).length > 1;
}

/**
 * Forward-fill run: when an edited day's LAST stop (where you sleep) changes,
 * the following days that still start where that day USED to end should be
 * offered the new end location — "Day 3 now ends in Annecy, also set Days
 * 4–6?". Walks unique dates after `editedDate` and returns the contiguous
 * run of dates to pre-check in the propagation modal.
 *
 * Include rules, per day D (first variant of its date):
 * - D starts at `oldLast` (case-insensitive) or has no location → include
 * - D already starts at `newLast` → halt (never propose a no-op)
 * - D starts somewhere else → halt (explicitly set — user's call)
 * - D's accommodation differs from the previous run day's (both named) →
 *   halt (a new hotel marks a new leg even if the city string still matches)
 * - D is itself multi-stop, or `hasOutboundTransport(date)` → include, then
 *   halt (that day ends elsewhere; days after it start from ITS destination)
 *
 * Pure — the caller supplies the transport check so this stays testable.
 */
export function computeForwardFillRun(tripDays, editedDate, oldLast, newLast, { hasOutboundTransport } = {}) {
  const norm = (s) => String(s || '').trim().toLowerCase();
  const from = norm(oldLast);
  const target = norm(newLast);
  if (!editedDate || !target || target === from) return [];

  const days = Array.isArray(tripDays) ? tripDays : [];
  const uniqueDates = [...new Set(days.map(d => d?.date).filter(Boolean))].sort();
  const startIdx = uniqueDates.indexOf(editedDate);
  if (startIdx === -1) return [];

  const repDay = (date) => days.find(d => d?.date === date) || {};
  let prevAccom = repDay(editedDate).accommodation?.name || '';

  const run = [];
  for (let i = startIdx + 1; i < uniqueDates.length; i++) {
    const date = uniqueDates[i];
    const day = repDay(date);
    const stops = getDayLocations(day);
    const first = norm(stops[0]?.name);

    if (first === target) break;
    if (first && first !== from) break;
    const accom = day.accommodation?.name || '';
    if (accom && prevAccom && accom !== prevAccom) break;

    run.push(date);
    prevAccom = accom || prevAccom;

    if (stops.length > 1) break;
    if (typeof hasOutboundTransport === 'function' && hasOutboundTransport(date)) break;
  }
  return run;
}

/**
 * Build the state patch for setting a day's stop list. Keeps the legacy
 * mirror fields (location/latitude/longitude = first stop) consistent so
 * every existing consumer of day.location keeps working.
 */
export function buildDayLocationsPatch(locations) {
  // Guard null/'' explicitly: Number(null) is 0, so a bare
  // Number.isFinite(Number(v)) would stamp coordinate-less stops onto
  // Null Island (0,0).
  const toNum = (v) => (v === null || v === undefined || v === ''
    ? null
    : (Number.isFinite(Number(v)) ? Number(v) : null));
  const cleaned = (Array.isArray(locations) ? locations : [])
    .map(l => (typeof l === 'string' ? { name: l, lat: null, lng: null } : l))
    .filter(l => l && String(l.name || '').trim())
    .map(l => ({
      name: String(l.name).trim(),
      lat: toNum(l.lat),
      lng: toNum(l.lng)
    }))
    .slice(0, MAX_DAY_LOCATIONS);
  const primary = cleaned[0] || null;
  return {
    locations: cleaned,
    location: primary ? primary.name : '',
    latitude: primary ? primary.lat : null,
    longitude: primary ? primary.lng : null
  };
}

/**
 * Normalized match key for a location, mirroring
 * server/services/visitorTipsService.js `locationKey()`. Keep the two in sync —
 * the server is authoritative (it writes the column), and a drifted client just
 * groups the review outline differently from where the tips actually land.
 *
 * A stay has no stable id, so location tips key on the NAME — and the raw name
 * is not safe to key on. Real data: one trip records the same two cities as
 * "Antwerp, Belgium" / "London, UK" on one day and "Antwerp" / "London" on the
 * next. Casefold, strip combining marks, keep the leading administrative
 * segment, and collapse everything that is not a letter or digit.
 *
 * The filter is \p{L}\p{N}, NOT a-z0-9: an ASCII-only class deletes every
 * character NFKD did not fold into ASCII, so "東京", "Москва" and "Αθήνα" would
 * all normalize to '' and key to each OTHER.
 */
export function locationKey(name) {
  const first = String(name || '').split(',')[0];
  return first
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}
